import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../../App';
import { MessageCircle, Phone, Video, ArrowLeft, Clock, User, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import MoodTagSelector from '../../components/MoodTagSelector';
import QueueStatus from '../../components/QueueStatus';

export default function MobileCareOptions() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [careGrades, setCareGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showQueue, setShowQueue] = useState(false);
  const [activeTab, setActiveTab] = useState('anonymous'); // anonymous | therapy

  useEffect(() => {
    fetchCareGrades();
  }, []);

  const fetchCareGrades = async () => {
    try {
      const response = await axios.get(`${API}/care-grades`);
      setCareGrades(response.data);
    } catch (error) {
      console.error('Failed to fetch care grades:', error);
      toast.error('Failed to load care options');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (channel) => {
    switch (channel) {
      case 'chat': return MessageCircle;
      case 'voice': return Phone;
      case 'video': return Video;
      default: return MessageCircle;
    }
  };

  const getGradientColor = (code) => {
    const colors = {
      G0: 'from-emerald-400 to-emerald-600',
      G1: 'from-teal-400 to-teal-600',
      G2: 'from-cyan-400 to-cyan-600',
      G3: 'from-sky-400 to-sky-600',
      G4: 'from-blue-400 to-blue-600',
      G5: 'from-violet-400 to-violet-600',
      G6: 'from-purple-400 to-purple-600'
    };
    return colors[code] || 'from-gray-400 to-gray-600';
  };

  const startAnonymousSession = async (grade) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to continue');
        navigate('/auth');
        return;
      }

      const response = await axios.post(
        `${API}/sessions`,
        {
          grade_code: grade.code,
          provider_id: null,
          mood_tags: selectedTags.length > 0 ? selectedTags : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Show queue status
      setActiveSessionId(response.data.id);
      setShowQueue(true);
      
      toast.success('You\'ve been added to the queue');
    } catch (error) {
      console.error('Failed to start session:', error);
      toast.error(error.response?.data?.detail || 'Failed to start session');
    }
  };

  const handleQueueStatusChange = (status) => {
    if (status === 'active' && activeSessionId) {
      // Find the session grade to determine navigation
      toast.success('A counselor has joined!');
      navigate(`/mobile-chat/${activeSessionId}`);
    }
  };

  const navigateToChat = () => {
    if (activeSessionId) {
      navigate(`/mobile-chat/${activeSessionId}`);
    }
  };

  // Split grades into anonymous (G0-G4) and therapy (G5-G6)
  const anonymousGrades = careGrades.filter(g => ['G0', 'G1', 'G2', 'G3', 'G4'].includes(g.code));
  const therapyGrades = careGrades.filter(g => ['G5', 'G6'].includes(g.code));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-24">
      {/* Header */}
      <div className="bg-primary text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/mobile-chats')} className="p-1" data-testid="back-button">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-heading font-semibold">{t('care_options')}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-100 sticky top-16 z-10">
        <button
          onClick={() => setActiveTab('anonymous')}
          className={`flex-1 py-3 text-center font-medium transition-colors ${
            activeTab === 'anonymous' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-[#718096]'
          }`}
          data-testid="tab-anonymous"
        >
          Anonymous Support
        </button>
        <button
          onClick={() => setActiveTab('therapy')}
          className={`flex-1 py-3 text-center font-medium transition-colors ${
            activeTab === 'therapy' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-[#718096]'
          }`}
          data-testid="tab-therapy"
        >
          Therapy (G5-G6)
        </button>
      </div>

      <div className="px-4 py-4">
        {/* Queue Status (when active) */}
        {showQueue && activeSessionId && (
          <div className="mb-4">
            <QueueStatus 
              sessionId={activeSessionId} 
              onStatusChange={handleQueueStatusChange}
            />
            <button
              onClick={navigateToChat}
              className="w-full mt-3 btn-primary"
              data-testid="go-to-chat"
            >
              Go to Chat
            </button>
          </div>
        )}

        {/* Anonymous Support Tab */}
        {activeTab === 'anonymous' && !showQueue && (
          <>
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-4 rounded-xl mb-4">
              <h2 className="font-heading font-semibold text-[#2D3748] mb-1">
                Stay Anonymous
              </h2>
              <p className="text-sm text-[#718096]">
                Get support without revealing your identity. Your privacy is protected.
              </p>
            </div>

            {/* Mood Tags */}
            <div className="card p-4 mb-4">
              <MoodTagSelector
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                maxTags={3}
              />
            </div>

            {/* Anonymous Care Grades (G0-G4) */}
            <div className="space-y-4">
              {anonymousGrades.map((grade) => {
                const Icon = getIcon(grade.channel);
                const price = grade.discount_price || grade.base_price;
                const hasDiscount = grade.discount_price && grade.discount_price < grade.base_price;

                return (
                  <div
                    key={grade.code}
                    data-testid={`care-grade-${grade.code}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
                  >
                    <div className={`h-1.5 bg-gradient-to-r ${getGradientColor(grade.code)}`}></div>
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getGradientColor(grade.code)} flex items-center justify-center`}>
                            <Icon size={22} className="text-white" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-[#2D3748]">
                              {grade.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Clock size={12} className="text-[#A0AEC0]" />
                              <span className="text-xs text-[#718096]">{grade.duration_minutes} min</span>
                              <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded capitalize">
                                {grade.channel}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {hasDiscount && (
                            <div className="text-xs text-[#A0AEC0] line-through">₹{grade.base_price}</div>
                          )}
                          <div className="text-lg font-bold text-primary">
                            {price === 0 ? 'Free' : `₹${price}`}
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-[#718096] mb-3">{grade.description}</p>

                      <button
                        onClick={() => startAnonymousSession(grade)}
                        data-testid={`start-${grade.code}`}
                        className="w-full py-2.5 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl font-medium active:scale-[0.98] transition-transform"
                      >
                        Start {grade.channel === 'chat' ? 'Chat' : grade.channel === 'voice' ? 'Call' : 'Session'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Therapy Tab (G5-G6) */}
        {activeTab === 'therapy' && (
          <>
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-violet-100 to-purple-100 p-4 rounded-xl mb-4">
              <h2 className="font-heading font-semibold text-[#2D3748] mb-1">
                Professional Therapy
              </h2>
              <p className="text-sm text-[#718096]">
                Connect with certified therapists for in-depth support. Identity verification required.
              </p>
            </div>

            {/* Find Therapist Button */}
            <button
              onClick={() => navigate('/find-therapist')}
              className="w-full card p-4 mb-4 flex items-center gap-4 hover:shadow-md transition-shadow"
              data-testid="find-therapist-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Search size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-[#2D3748]">Find a Therapist</h3>
                <p className="text-sm text-[#718096]">Search by specialization, rating & availability</p>
              </div>
            </button>

            {/* Therapy Grades */}
            <div className="space-y-4">
              {therapyGrades.map((grade) => {
                const Icon = getIcon(grade.channel);
                const price = grade.discount_price || grade.base_price;

                return (
                  <div
                    key={grade.code}
                    data-testid={`care-grade-${grade.code}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
                  >
                    <div className={`h-1.5 bg-gradient-to-r ${getGradientColor(grade.code)}`}></div>
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getGradientColor(grade.code)} flex items-center justify-center`}>
                            <Icon size={22} className="text-white" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-[#2D3748]">
                              {grade.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Clock size={12} className="text-[#A0AEC0]" />
                              <span className="text-xs text-[#718096]">{grade.duration_minutes} min</span>
                              <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">
                                Video
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-primary">₹{price}</div>
                        </div>
                      </div>

                      <p className="text-sm text-[#718096] mb-3">{grade.description}</p>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                          <User size={10} />
                          Identity Required
                        </span>
                      </div>

                      <button
                        onClick={() => navigate('/find-therapist')}
                        data-testid={`book-${grade.code}`}
                        className="w-full py-2.5 border-2 border-primary text-primary rounded-xl font-medium hover:bg-primary/5 transition-colors"
                      >
                        Find a Therapist
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Crisis Banner */}
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Need Immediate Help?</h3>
              <p className="text-sm text-red-700 mb-2">
                If you're in crisis, call Tele-MANAS helpline (24/7)
              </p>
              <div className="flex gap-2">
                <a
                  href="tel:14416"
                  className="inline-block px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium"
                  data-testid="call-telemanas"
                >
                  Call 14416
                </a>
                <button
                  onClick={() => navigate('/tele-manas')}
                  className="inline-block px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium"
                  data-testid="learn-more-telemanas"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 flex justify-around">
        <button
          onClick={() => navigate('/mobile-chats')}
          className="flex flex-col items-center py-2 px-4 text-[#718096]"
          data-testid="nav-chats"
        >
          <MessageCircle size={24} />
          <span className="text-xs mt-1">{t('chats')}</span>
        </button>
        <button
          onClick={() => navigate('/mobile-care-options')}
          className="flex flex-col items-center py-2 px-4 text-primary"
          data-testid="nav-care"
        >
          <Clock size={24} />
          <span className="text-xs mt-1">{t('care_options')}</span>
        </button>
        <button
          onClick={() => navigate('/profile')}
          className="flex flex-col items-center py-2 px-4 text-[#718096]"
          data-testid="nav-profile"
        >
          <User size={24} />
          <span className="text-xs mt-1">{t('profile')}</span>
        </button>
      </div>
    </div>
  );
}
